import React, { useEffect, useState } from "react";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Button from "@material-ui/core/Button";
import { saveAs } from "file-saver";
import moment from "moment";

const PACKAGE_PRICES = {
  Worldclass: 40,
  Legendary: 120,
  Pro: 20,
};

// Add this mapping constant after the PACKAGE_PRICES
const PACKAGE_MAPPING = {
  1: "Worldclass",
  2: "Legendary",
  3: "Pro",
};

const UpgradeInsightsDashboard = () => {
  const [upgradeStats, setUpgradeStats] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [upgradesData, setUpgradesData] = useState([]);

  const years = Array.from(
    { length: new Date().getFullYear() - 2018 + 1 },
    (_, i) => 2018 + i
  );

  useEffect(() => {
    // Create start and end timestamps for the selected year
    const startDate = new Date(selectedYear, 0, 1); // January 1st
    const endDate = new Date(selectedYear, 11, 31, 23, 59, 59, 999); // December 31st

    window.firebase
      .firestore()
      .collection("upgrades")
      .where("payed", "==", true)
      .where("timestamp", ">=", startDate)
      .where("timestamp", "<=", endDate)
      .get()
      .then((querySnapshot) => {
        const upgrades = [];
        querySnapshot.forEach((doc) => {
          const upgradeData = doc.data();
          upgradeData.id = doc.id; // Store document ID
          upgrades.push(upgradeData);
        });
        const stats = calculateUpgradeStats(upgrades);
        setUpgradeStats(stats);
        setUpgradesData(upgrades); // Store raw upgrade data for export
      });
  }, [selectedYear]); // Add selectedYear as dependency

  // First modify the calculateUpgradeStats function to include daily stats:
  const calculateUpgradeStats = (upgrades) => {
    const stats = {};

    upgrades.forEach((upgrade) => {
      const timestamp = upgrade.timestamp.toDate();
      const year = timestamp.getFullYear();
      const packageType = PACKAGE_MAPPING[upgrade.package] || "Unknown";
      const country = upgrade.country;
      const uid = upgrade.uid;
      const month = timestamp.getMonth() + 1;
      const day = timestamp.getDate();

      if (!stats[year]) {
        stats[year] = {
          totalUpgrades: 0,
          totalRevenue: 0,
          packages: {},
          countries: {},
          monthlyUpgrades: {},
          uniqueUsers: new Set(),
        };
      }

      // Increment totalUpgrades counter
      stats[year].totalUpgrades += 1;

      const revenue = PACKAGE_PRICES[packageType] || 0;
      stats[year].totalRevenue += revenue;

      if (!stats[year].packages[packageType]) {
        stats[year].packages[packageType] = 0;
      }
      stats[year].packages[packageType] += 1;

      if (!stats[year].countries[country]) {
        stats[year].countries[country] = 0;
      }
      stats[year].countries[country] += 1;

      // Add monthly and daily breakdown
      if (!stats[year].monthlyUpgrades[month]) {
        stats[year].monthlyUpgrades[month] = {
          total: 0,
          totalRevenue: 0,
          dailyUpgrades: {},
          dailyRevenue: {},
        };
      }

      if (!stats[year].monthlyUpgrades[month].dailyUpgrades[day]) {
        stats[year].monthlyUpgrades[month].dailyUpgrades[day] = 0;
        stats[year].monthlyUpgrades[month].dailyRevenue[day] = 0;
      }

      stats[year].monthlyUpgrades[month].total += 1;
      stats[year].monthlyUpgrades[month].totalRevenue += revenue;
      stats[year].monthlyUpgrades[month].dailyUpgrades[day] += 1;
      stats[year].monthlyUpgrades[month].dailyRevenue[day] += revenue;

      stats[year].uniqueUsers.add(uid);
    });

    // Convert sets to counts for unique users per year
    Object.keys(stats).forEach((year) => {
      stats[year].uniqueUsers = stats[year].uniqueUsers.size;
    });

    return stats;
  };

  // CSV export function
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const exportToCSV = () => {
    if (upgradesData.length === 0) {
      alert("No upgrade data available to export.");
      return;
    }

    // Define CSV headers
    const headers = [
      "Document ID",
      "address",
      "asACompany",
      "btwNum",
      "companyName",
      "country",
      "currentPackage",
      "email",
      "extraRef",
      "firstName",
      "invoiceId",
      "invoiceStatus",
      "ip_address",
      "language",
      "lastName",
      "method",
      "package",
      "payByInvoice",
      "payed",
      "place",
      "price",
      "revenue",
      "status",
      "timestamp",
      "tournamentId",
      "uid",
      "vat_percentage",
      "zipCode",
    ];

    // Create CSV rows
    const rows = upgradesData.map((upgrade) => {
      return [
        escapeCSVValue(upgrade.id || ""),
        escapeCSVValue(upgrade.address || ""),
        escapeCSVValue(upgrade.asACompany || ""),
        escapeCSVValue(upgrade.btwNum || ""),
        escapeCSVValue(upgrade.companyName || ""),
        escapeCSVValue(upgrade.country || ""),
        escapeCSVValue(upgrade.currentPackage || ""),
        escapeCSVValue(upgrade.email || ""),
        escapeCSVValue(upgrade.extraRef || ""),
        escapeCSVValue(upgrade.firstName || ""),
        escapeCSVValue(upgrade.invoiceId || ""),
        escapeCSVValue(upgrade.invoiceStatus || ""),
        escapeCSVValue(upgrade.ip_address || ""),
        escapeCSVValue(upgrade.language || ""),
        escapeCSVValue(upgrade.lastName || ""),
        escapeCSVValue(upgrade.method || ""),
        escapeCSVValue(upgrade.package || ""),
        escapeCSVValue(upgrade.payByInvoice || ""),
        escapeCSVValue(upgrade.payed || ""),
        escapeCSVValue(upgrade.place || ""),
        escapeCSVValue(upgrade.price || ""),
        escapeCSVValue(upgrade.revenue || ""),
        escapeCSVValue(upgrade.status || ""),
        escapeCSVValue(
          upgrade.timestamp
            ? moment(upgrade.timestamp.toDate()).format("YYYY-MM-DD HH:mm:ss")
            : ""
        ),
        escapeCSVValue(upgrade.tournamentId || ""),
        escapeCSVValue(upgrade.uid || ""),
        escapeCSVValue(upgrade.vat_percentage || ""),
        escapeCSVValue(upgrade.zipCode || ""),
      ];
    });

    // Combine headers and rows
    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const filename = `upgrades_export_${selectedYear}_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`;
    saveAs(blob, filename);
  };

  // Add a new component for the monthly breakdown
  const MonthlyBreakdown = ({ month, data }) => {
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
      setExpanded(!expanded);
    };

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div style={{ marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={handleExpandClick}
            style={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
          <span>
            {monthNames[month - 1]}: {data.total} upgrades (€
            {data.totalRevenue.toLocaleString()})
          </span>
        </div>
        <Collapse in={expanded}>
          <div style={{ marginLeft: "40px" }}>
            {Object.entries(data.dailyUpgrades)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([day, count]) => (
                <div key={day}>
                  Day {day}: {count} upgrades - €
                  {data.dailyRevenue[day].toLocaleString()}
                </div>
              ))}
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <div>
      <h2>Upgrade Insights Dashboard</h2>

      <FormControl fullWidth>
        <InputLabel id="year-select-label">Select Year</InputLabel>
        <Select
          labelId="year-select-label"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
        >
          {years.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div style={{ marginBottom: "20px", marginTop: "20px" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={exportToCSV}
          disabled={upgradesData.length === 0}
        >
          Export to CSV ({upgradesData.length} upgrades)
        </Button>
      </div>

      <h3>Upgrade Insights for {selectedYear}</h3>
      {Object.entries(upgradeStats).map(([year, data]) => (
        <div key={year}>
          <h4>{year}</h4>
          <p>Total Upgrades: {data.totalUpgrades}</p>
          <p>Total Revenue: €{data.totalRevenue.toLocaleString()}</p>
          <p>Unique Purchasers: {data.uniqueUsers}</p>
          <h5>Packages:</h5>
          <ul>
            {Object.entries(data.packages).map(([packageType, count]) => (
              <li key={packageType}>
                {packageType}: {count} upgrades
              </li>
            ))}
          </ul>
          <h5>Monthly Upgrades:</h5>
          <div>
            {Object.entries(data.monthlyUpgrades)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([month, monthData]) => (
                <MonthlyBreakdown
                  key={month}
                  month={Number(month)}
                  data={monthData}
                />
              ))}
          </div>

          <h5>Countries:</h5>
          <ul>
            {Object.entries(data.countries).map(([country, count]) => (
              <li key={country}>
                {country}: {count} upgrades
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default UpgradeInsightsDashboard;
